// @ts-nocheck
/**
 * ObtainTab - 入手情報タブ
 *
 * カードの入手方法、パック確率、商人情報を表示
 */

import React, { useMemo } from 'react';
import { COLORS } from '../../../styles/cardDexStyles';
import { ATTRIBUTE_PACK, SLOT_5_RATES } from '../../../../data/constants';
import { MERCHANTS, ATTRIBUTE_MERCHANTS, COLLECTORS } from '../../../../merchant/merchantData';
import { ATTRIBUTE_COLORS } from '../../../../../utils/constants';

// ========================================
// 商人マッチング
// ========================================

/**
 * カードに関連する商人を取得
 */
const getRelatedMerchants = (card) => {
  const results = {
    attributeMerchant: null,
    collectors: [],
  };

  // 属性商人を探す
  for (const merchant of ATTRIBUTE_MERCHANTS) {
    if (merchant.specialty?.type === 'attribute' && merchant.specialty?.value === card.attribute) {
      results.attributeMerchant = merchant;
      break;
    }
  }

  // コレクターを探す
  for (const collector of COLLECTORS) {
    if (!collector.specialty) continue;

    const { type, value } = collector.specialty;
    const values = Array.isArray(value) ? value : [value];

    let matches = false;

    if (type === 'attribute') {
      matches = values.includes(card.attribute);
    } else if (type === 'category') {
      // カテゴリマッチング
      const cardCategory = card.category || '';
      matches = values.some((v) => cardCategory.includes(v));
    } else if (type === 'name') {
      // 名前マッチング
      matches = values.some((v) => card.name.includes(v));
    } else if (type === 'nameOrCategory') {
      // 名前またはカテゴリマッチング
      const cardCategory = card.category || '';
      matches = values.some((v) => card.name.includes(v) || cardCategory.includes(v));
    }

    if (matches) {
      results.collectors.push(collector);
    }
  }

  return results;
};

/**
 * 出現曜日を取得
 */
const getAppearanceDay = (appearance) => {
  if (!appearance || appearance === 'always') return '常駐';
  if (appearance === 'random') return 'ランダム';
  if (appearance === 'daily') return '常駐';
  if (appearance.weekday !== undefined) {
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    return `${days[appearance.weekday]}曜日`;
  }
  return '不定期';
};

// ========================================
// パック確率セクション
// ========================================

const PackProbabilitySection = ({ card, allCardsCount, attributeCardsCount }) => {
  // 枠5（R以上確定枠）のレアリティ出現率
  const slot5Total = Object.values(SLOT_5_RATES).reduce((sum, rate) => sum + rate, 0);

  // 各パックでの出現確率を計算
  const probabilities = useMemo(() => {
    // 闇鍋パック: 5枠全てで全カードから抽選
    // 1枠あたり 1/全カード数 の確率、5枠なので約5倍
    const yaminabePerSlot = 1 / allCardsCount;
    const yaminabeTotal = 1 - Math.pow(1 - yaminabePerSlot, 5);

    // 属性パック: 属性カード数で計算
    const isAttributePackTarget = ATTRIBUTE_PACK.ATTRIBUTES.includes(card.attribute);
    let attributePerSlot = 0;
    let attributeTotal = 0;
    if (isAttributePackTarget && attributeCardsCount > 0) {
      attributePerSlot = 1 / attributeCardsCount;
      attributeTotal = 1 - Math.pow(1 - attributePerSlot, 5);
    }

    // ロイヤルパック: SR以上のみ出現
    // 基礎価値が高いカード（コスト高・高スタッツ）が高レアリティで出やすい
    // ここでは簡易的に「ロイヤルで出る可能性」を表示
    const royalEligible = true; // 全カードがロイヤルで出る可能性がある（レアリティによる）

    return {
      yaminabe: yaminabeTotal,
      attribute: attributeTotal,
      isAttributePackTarget,
      royalEligible,
    };
  }, [card, allCardsCount, attributeCardsCount]);

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ パック確率</div>
      <div style={sectionStyles.content}>
        {/* 闇鍋パック */}
        <div style={packStyles.row}>
          <div style={packStyles.packName}>
            <span style={packStyles.icon}>📦</span>
            闇鍋パック
          </div>
          <div style={packStyles.rate}>
            {(probabilities.yaminabe * 100).toFixed(2)}%
          </div>
        </div>

        {/* 属性パック */}
        <div style={packStyles.row}>
          <div style={packStyles.packName}>
            <span style={packStyles.icon}>🎴</span>
            属性パック
            {probabilities.isAttributePackTarget && (
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '11px',
                  color: ATTRIBUTE_COLORS[card.attribute]?.primary || COLORS.textSecondary,
                }}
              >
                ({card.attribute})
              </span>
            )}
          </div>
          <div style={packStyles.rate}>
            {probabilities.isAttributePackTarget ? (
              `${(probabilities.attribute * 100).toFixed(2)}%`
            ) : (
              <span style={{ color: COLORS.textSecondary }}>対象外</span>
            )}
          </div>
        </div>

        {/* ロイヤルパック */}
        <div style={packStyles.row}>
          <div style={packStyles.packName}>
            <span style={packStyles.icon}>👑</span>
            ロイヤルパック
          </div>
          <div style={packStyles.rate}>
            <span style={{ color: COLORS.textGold }}>SR以上で出現</span>
          </div>
        </div>

        {/* 注釈 */}
        <div style={{ marginTop: '12px', fontSize: '11px', color: COLORS.textSecondary }}>
          ※ 確率は5枚中1枚以上出る確率（レアリティは別途抽選）
        </div>
      </div>
    </div>
  );
};

// ========================================
// 商人情報セクション
// ========================================

const MerchantSection = ({ card }) => {
  const { attributeMerchant, collectors } = useMemo(() => getRelatedMerchants(card), [card]);

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ 商人情報</div>
      <div style={sectionStyles.content}>
        {/* 属性商人 */}
        {attributeMerchant ? (
          <div style={merchantStyles.row}>
            <div style={merchantStyles.header}>
              <span style={merchantStyles.icon}>{attributeMerchant.icon}</span>
              <span style={merchantStyles.name}>{attributeMerchant.name}</span>
              <span style={merchantStyles.type}>（属性商人）</span>
            </div>
            <div style={merchantStyles.detail}>
              <span style={merchantStyles.label}>出現:</span>
              <span style={merchantStyles.value}>{getAppearanceDay(attributeMerchant.appearance)}</span>
            </div>
            <div style={merchantStyles.description}>
              {card.attribute}属性カードを専門に扱う
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
              専門の属性商人なし
            </div>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '4px' }}>
              マルクス（一般商人）から購入可能
            </div>
          </div>
        )}

        {/* コレクター */}
        {collectors.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '12px', color: COLORS.textGold, marginBottom: '8px' }}>
              興味を持つコレクター:
            </div>
            {collectors.map((collector) => (
              <div key={collector.id} style={merchantStyles.row}>
                <div style={merchantStyles.header}>
                  <span style={merchantStyles.icon}>{collector.icon}</span>
                  <span style={merchantStyles.name}>{collector.name}</span>
                  <span style={merchantStyles.type}>（コレクター）</span>
                </div>
                <div style={merchantStyles.detail}>
                  <span style={merchantStyles.label}>出現:</span>
                  <span style={merchantStyles.value}>{getAppearanceDay(collector.appearance)}</span>
                </div>
                <div style={merchantStyles.description}>
                  {collector.specialty?.value && (
                    <>
                      専門: {Array.isArray(collector.specialty.value)
                        ? collector.specialty.value.join('、')
                        : collector.specialty.value}
                    </>
                  )}
                </div>
              </div>
            ))}
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '8px' }}>
              ※ コレクターは高値で買い取ってくれます
            </div>
          </div>
        )}

        {collectors.length === 0 && (
          <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
            特に興味を持つコレクターなし
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================
// ダンジョン情報セクション（Phase 4で拡張）
// ========================================

const DungeonSection = ({ card }) => {
  // Phase 4で実装予定
  // TwinKeyのドロップテーブルと連携
  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ ダンジョン情報</div>
      <div style={sectionStyles.content}>
        <div style={{ fontSize: '13px', color: COLORS.textPrimary, marginBottom: '8px' }}>
          🚪 ツインキーダンジョン
        </div>
        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
          このカードを鍵として使用可能
        </div>
        <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '8px' }}>
          ※ ダンジョンドロップ詳細は「ダンジョン」タブで確認
        </div>
      </div>
    </div>
  );
};

// ========================================
// 対戦報酬セクション
// ========================================

const BattleRewardSection = () => {
  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ 対戦報酬</div>
      <div style={sectionStyles.content}>
        <div style={{ fontSize: '13px', color: COLORS.textPrimary, marginBottom: '4px' }}>
          ⚔️ 勝利ボーナス
        </div>
        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
          対戦に勝利すると闇鍋パック1個獲得
        </div>
      </div>
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================

const ObtainTab = ({ card, allCards }) => {
  // 全カード数と同属性カード数を計算
  const { allCardsCount, attributeCardsCount } = useMemo(() => {
    const attrCards = allCards.filter((c) => c.attribute === card.attribute);
    return {
      allCardsCount: allCards.length,
      attributeCardsCount: attrCards.length,
    };
  }, [allCards, card.attribute]);

  return (
    <div>
      {/* パック確率 */}
      <PackProbabilitySection
        card={card}
        allCardsCount={allCardsCount}
        attributeCardsCount={attributeCardsCount}
      />

      {/* 商人情報 */}
      <MerchantSection card={card} />

      {/* ダンジョン情報 */}
      <DungeonSection card={card} />

      {/* 対戦報酬 */}
      <BattleRewardSection />
    </div>
  );
};

// ========================================
// スタイル定義
// ========================================

const sectionStyles = {
  container: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '14px',
    color: COLORS.textGold,
    marginBottom: '12px',
  },
  content: {
    padding: '12px',
    background: 'rgba(30, 30, 50, 0.6)',
    borderRadius: '8px',
  },
};

const packStyles = {
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${COLORS.borderSecondary}`,
  },
  packName: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: COLORS.textPrimary,
  },
  icon: {
    marginRight: '8px',
    fontSize: '16px',
  },
  rate: {
    fontSize: '13px',
    color: COLORS.textPrimary,
    fontWeight: 'bold',
  },
};

const merchantStyles = {
  row: {
    padding: '8px 0',
    borderBottom: `1px solid ${COLORS.borderSecondary}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
  },
  icon: {
    marginRight: '8px',
    fontSize: '18px',
  },
  name: {
    fontSize: '14px',
    color: COLORS.textPrimary,
    fontWeight: 'bold',
  },
  type: {
    marginLeft: '8px',
    fontSize: '11px',
    color: COLORS.textSecondary,
  },
  detail: {
    fontSize: '12px',
    marginBottom: '4px',
  },
  label: {
    color: COLORS.textSecondary,
    marginRight: '8px',
  },
  value: {
    color: COLORS.textPrimary,
  },
  description: {
    fontSize: '11px',
    color: COLORS.textSecondary,
  },
};

export default ObtainTab;
